import React, { ChangeEvent, FC, FormEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    useCreateQcRuleMutation,
    useDeleteQcRuleByIdMutation,
    useGetQcRuleByIdQuery,
    useUpdateQcRuleMutation
} from "../qcRuleApi";
import { useGetEnumsQuery } from "../../../../app/globalApi";
import { useGetCodesListQuery } from "../../../sysAdmin/code/codeApi";
import { useGetAllTenantsQuery } from "../../../customer/tenantApi";
import { handleRTKQuery } from "../../../../helpers/utils/RtkQueryUtils";
import { createOptionListForSelectTag } from "../../../../helpers/utils/Utils";
import { hasPermission } from "../../../../helpers/utils/AccessControlUtils";
import { Routes as AppRoutes } from "../../../../routes";
import Breadcrumb, { Crumb } from "../../../../components/common/Breadcrumbs";
import AuditableInfoButton from "../../../../components/common/form/AuditableInfoButton";
import InputField from "../../../../components/common/form/InputField";
import TextAreaField from "../../../../components/common/form/TextAreaField";
import SelectField from "../../../../components/common/form/SelectField";
import ButtonTray from "../../../../components/common/form/ButtonTray";
import Form from "../../../../components/common/form/Form";
import { useLazyGetCustomerByTenantAndLevelQuery } from "../../../customer/customerApi";
import ConfirmationModal from "../../../../components/layout/modal/ConfirmationModal";
import { FaTrash } from "react-icons/fa6";
import IconButton from "../../../../components/common/form/IconButton";
import ModalButton from "../../../../components/common/form/ModalButton";

interface Props {
    crumbs: Crumb[];
    title: string;
    btnText: string;
    isEditable?: boolean;
}

const QcLogicFormField: FC<Props> = ({ crumbs, title, btnText, isEditable }) => {
    const { qcLogicId } = useParams();
    const navigate = useNavigate();
    const [qcLogicData, setQcLogicData] = useState<any>({});

    const { data: qcRule } = useGetQcRuleByIdQuery(qcLogicId);
    const { data: enums } = useGetEnumsQuery("");
    const { data: codeList } = useGetCodesListQuery("");
    const { data: tenants } = useGetAllTenantsQuery("");
    const [updateQcLogicApi, updateQcLogicApiResponse] = useUpdateQcRuleMutation();
    const [customerDataApi, customerDataApiResponse] = useLazyGetCustomerByTenantAndLevelQuery();
    const [createQcLogic, createQcLogicApiResponse] = useCreateQcRuleMutation();
    const [deleteApi, { isLoading }] = useDeleteQcRuleByIdMutation();

    useEffect(() => {
        if (qcRule) {
            setQcLogicData({
                ...qcRule,
                type: qcRule.type ? { value: qcRule.type, label: qcRule.type } : null,
                code: qcRule.code ? { value: qcRule.code.id, label: qcRule.code.name } : null,
                productTypes: qcRule.productTypes?.map((pt: any) => ({ value: pt, label: pt })) || [],
                tenant: qcRule.tenant ? { value: qcRule.tenant, label: qcRule.tenant } : null,
                customer: qcRule?.customer
                    ? {
                        value: qcRule?.customer?.id,
                        label: qcRule?.customer?.customerName || qcRule?.customer?.customerName.toString(),
                    }
                    : null,
            });

            if (qcRule.tenant) {
                customerDataApi({ tenantName: qcRule.tenant, level: "CUSTOMER" });
            }
        }
    }, [qcRule]);

    const handleOnChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { name: string; value: any }) => {
        const { name, value } = "target" in e ? e.target : e;
        setQcLogicData((prev: any) => ({ ...prev, [name]: value }));

        if (name === "tenant" && value) {
            customerDataApi({ tenantName: value.value, level: "CUSTOMER" });
        }
    };


    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const updatedData = {
            ...qcLogicData,
            type: qcLogicData.type?.value,
            tenant: qcLogicData.tenant?.value,
            customerId: qcLogicData.customer?.value,
            code: qcLogicData.code ? { id: qcLogicData.code.value } : null, // **FIXED: Send `code` as object**
            productTypes: qcLogicData.productTypes?.map((pt: { value: any }) => pt.value),
        };

        const createOrUpdateBillRelease = async () => {
            if (btnText === "Create") {
                return await createQcLogic(updatedData).unwrap();
            } else {
                return await updateQcLogicApi({
                    id: qcLogicId,
                    requestBody: updatedData,
                }).unwrap();
            }
        };


        await handleRTKQuery(createOrUpdateBillRelease, (response: any) => {
            if (response?.success || response?.data?.success) {
                setQcLogicData({});
                navigate(AppRoutes.QCLogics.path);
            }
        });
    };

    const handleDeleteJurisdiction = async () => {
        if (qcLogicId) {
            await deleteApi(qcLogicId).unwrap();
        }
        navigate(AppRoutes.QCLogics.path);
    };

    return (
        <div className="px-4 py-2">
            <div className="flex justify-between mb-3">
                <Breadcrumb crumbs={crumbs} />
                {
                    qcLogicId && (
                        <div className={"justify-between sm:block md:flex lg:flex"}>
                            <ModalButton
                                triggerButton={
                                    <IconButton
                                        icon={<FaTrash />}
                                        title={"Delete QC Logics"}
                                        hasBackground={true}
                                        className="mr-2"
                                    />
                                }
                                modal={
                                    <ConfirmationModal
                                        show={true}
                                        setShow={() => {
                                        }}
                                        title={"Delete QC Logics"}
                                        bodyMessage={"Are you sure you want to delete this Qc Logics?"}
                                        onSubmit={() => handleDeleteJurisdiction()}
                                        isLoading={isLoading}
                                    />
                                }
                            />
                            {hasPermission(["AUDIT-V"]) &&
                                <AuditableInfoButton obj={qcLogicData} />
                            }
                        </div>
                    )
                }

            </div>

            <div className="flex justify-center mt-3">
                <div className="bg-text-0 lg:w-4/6 sm:w-full p-5 rounded-md shadow-[0_0_10px_-2px_rgba(0,0,0,0.2)]">
                    <h3 className="text-xl font-semibold text-surface-0 pb-3 border-b border-surface-7">{title}</h3>

                    <Form onSubmit={handleSubmit} fields={
                        <>
                            <InputField
                                name="name"
                                label="Name"
                                onChange={handleOnChange}
                                value={qcLogicData?.name || ""}
                                required
                                placeholder="Enter Name..."
                            />

                            <SelectField
                                label="Code"
                                value={qcLogicData?.code}
                                options={createOptionListForSelectTag(codeList, "name", "id")}
                                onChange={(option) => handleOnChange({ name: "code", value: option })}
                                required
                            />

                            <SelectField
                                label="Product Type"
                                value={qcLogicData?.productTypes}
                                options={createOptionListForSelectTag(enums?.["ProductType"], "value", "constant")}
                                onChange={(option) => handleOnChange({ name: "productTypes", value: option })}
                                isMulti
                                required
                            />

                            <SelectField
                                label="Tenant"
                                value={qcLogicData?.tenant}
                                options={createOptionListForSelectTag(tenants, "name", "name")}
                                onChange={(option) => handleOnChange({ name: "tenant", value: option })}
                            />

                            <SelectField
                                label="Customer"
                                value={qcLogicData?.customer}  // Ensure customer value is pre-populated correctly
                                options={createOptionListForSelectTag(customerDataApiResponse.data, "customerName", "id")}
                                onChange={(option) => handleOnChange({ name: "customer", value: option })}
                            />


                            <TextAreaField
                                label="Description"
                                name="description"
                                value={qcLogicData?.description || ""}
                                onChange={handleOnChange}
                                placeholder="Enter description..."
                            />

                            {btnText !== "Create" && (
                                <InputField
                                    name="enabled"
                                    label="Is Enabled"
                                    type="checkbox"
                                    checked={qcLogicData.enabled}
                                    onChange={() =>
                                        setQcLogicData((prev: { enabled: any }) => ({
                                            ...prev,
                                            enabled: !prev.enabled,
                                        }))
                                    }
                                />
                            )}

                            <div className="col-span-12 md:grid md:grid-cols-12 gap-3">
                                <div className="col-span-3 sm:mt-2 lg:mt-0 mt-2 flex items-center">
                                </div>
                                <div className="relative col-span-9">
                                    <div className={"mt-4 flex"}>
                                        <ButtonTray buttons={[
                                            {
                                                buttonProps: {
                                                    btnText: "Cancel",
                                                    onClick: () => navigate(AppRoutes.QCLogics.path),
                                                    type: "cancel"
                                                },
                                                buttonType: "button"
                                            },
                                            {
                                                buttonProps: {
                                                    btnText: "Submit",
                                                    type: "submit",
                                                    isLoading: (createQcLogicApiResponse.isLoading || updateQcLogicApiResponse.isLoading),
                                                    isVisible: hasPermission(["CANNOTE-U"])
                                                },
                                                buttonType: "button"
                                            }
                                        ]} align="center" gap={2} />
                                    </div>
                                </div>
                            </div>

                        </>
                    } />
                </div>
            </div>
        </div>
    );
};

export default QcLogicFormField;
